import { useTranslations } from "next-intl";
import { useId } from "react";
import { RadioSelectItem } from "@/components/radio-select";
import { SelectTriggerLabel } from "@/components/select-trigger-label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectValue } from "@/components/ui/select";
import type { ProductReviewsSort } from "@/features/rating/types/product-rating.type";

const RATING_OPTIONS = [5, 4, 3, 2, 1] as const;
const ALL_RATINGS_VALUE = "0";

type RatingFiltersProps = {
  sort: ProductReviewsSort;
  rating: number | undefined;
  withText: boolean;
  onSortChange: (sort: ProductReviewsSort) => void;
  onRatingChange: (rating: number | undefined) => void;
  onWithTextChange: (withText: boolean) => void;
};

export const RatingFilters = ({
  sort,
  rating,
  withText,
  onSortChange,
  onRatingChange,
  onWithTextChange,
}: RatingFiltersProps) => {
  const t = useTranslations("rating.filters");
  const withTextId = useId();

  const ratingValue = rating?.toString() ?? ALL_RATINGS_VALUE;

  const handleRatingChange = (value: string) => {
    onRatingChange(value === ALL_RATINGS_VALUE ? undefined : Number(value));
  };

  return (
    <div className="flex w-full flex-col gap-y-4 p-4 md:p-6">
      <div className="grid w-full place-items-center gap-y-2 md:grid-cols-2 md:gap-x-4">
        <Select
          onValueChange={(value) => onSortChange(value as ProductReviewsSort)}
          value={sort}
        >
          <SelectTriggerLabel
            className="font-medium focus:ring-0 focus-visible:ring-0"
            label={t("sortBy")}
          >
            <SelectValue />
          </SelectTriggerLabel>
          <SelectContent>
            <RadioSelectItem
              label={t("newestToOldest")}
              optionValue="desc"
              value={sort}
            />
            <RadioSelectItem
              label={t("oldestToNewest")}
              optionValue="asc"
              value={sort}
            />
          </SelectContent>
        </Select>
        <Select onValueChange={handleRatingChange} value={ratingValue}>
          <SelectTriggerLabel
            className="font-medium focus:ring-0 focus-visible:ring-0"
            label={t("filterByRating")}
          >
            <SelectValue />
          </SelectTriggerLabel>
          <SelectContent>
            <RadioSelectItem
              label={t("noRating")}
              optionValue={ALL_RATINGS_VALUE}
              value={ratingValue}
            />
            {RATING_OPTIONS.map((option) => (
              <RadioSelectItem
                key={option}
                label={t("stars", { count: option })}
                optionValue={option.toString()}
                value={ratingValue}
              />
            ))}
          </SelectContent>
        </Select>
      </div>
      <label
        className="flex w-full cursor-pointer items-center gap-x-2"
        htmlFor={withTextId}
      >
        <Checkbox
          checked={withText}
          id={withTextId}
          onCheckedChange={(checked) => onWithTextChange(checked === true)}
          className="h-5 w-5 border-border bg-brand-white"
        />
        {t("withTextOnly")}
      </label>
    </div>
  );
};
